/*
 * Copyright (c) 2025, Gather
 * https://gather.life
 */

import { useCallback, type ReactNode, useRef, memo } from 'react';
import Box from '@mui/material/Box/Box';
import { useFormik } from 'formik';

import { Skeleton } from '@marketplace/gather-ui/components';
import { AppSpacing } from '@marketplace/gather-ui/theme';

import { Search, type UpdateSearchValueFunc } from './search.component';
import { SearchDropdownProvider } from './dropdown/search-dropdown.provider';
import { InputWrapper } from './search-input/search-input.component';
import { useCatalogSearchStore } from '../catalog-search-store.provider';
import { useCatalogFilterStore } from '../../components/catalog-filter-store.provider';
import { type SearchType } from '../../store/catalog-search.stored-state';
import { type RequiredBodyType, type RequiredMakeModel } from '../../store/catalog-filter.store';
import {
  FixedBottomToast,
  NotificationProvider,
} from '@marketplace/gather-ui/providers/notification-provider';

// This makes the toast larger so that the error messages for deleting search history
// appear on a single line
const alertOverridesSx = { width: { sm: 'fit-content', md: 'fit-content' } };

type SearchProps = {
  disabled: boolean;
  loading: boolean;
  onSubmit: () => void;
};

export const SearchView = memo(function SearchView({
  disabled,
  loading,
  onSubmit,
}: SearchProps): ReactNode {
  const { query, updateSearchQuery, updateSearchHistoryId } = useCatalogSearchStore((state) => ({
    query: state.query,
    updateSearchQuery: state.updateSearchQuery,
    updatePage: state.updatePage,
    updateSearchHistoryId: state.updateSearchHistoryId,
  }));

  const resetFilters = useCatalogFilterStore((state) => state.resetFilters);

  const searchTypeRef = useRef<SearchType>('DEFAULT_SEARCH');
  const selectedFilterRef = useRef<RequiredMakeModel | RequiredBodyType | null>(null);

  const {
    values: formikValues,
    handleBlur,
    handleSubmit,
    setFieldValue,
    submitForm,
  } = useFormik({
    initialValues: {
      search: query.query,
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      onSubmit();

      // Order matters: first reset searchHistoryId, then visual filters, finally update search query
      updateSearchHistoryId(null);
      // When a new search is preformed, the filters should be reset but not the location
      resetFilters({ resetLocation: false, useYearDefaults: false });

      if (selectedFilterRef.current == null) {
        updateSearchQuery(values.search, searchTypeRef.current);
      } else if ('name' in selectedFilterRef.current) {
        updateSearchQuery(values.search, searchTypeRef.current, {
          bodyType: [selectedFilterRef.current],
        });
      } else if ('make' in selectedFilterRef.current) {
        updateSearchQuery(values.search, searchTypeRef.current, {
          makeModels: [selectedFilterRef.current],
        });
      }
    },
  });

  const updateSearchValue: UpdateSearchValueFunc = useCallback(
    async ({ searchValue, searchType, selectedFilter, autoSubmit }) => {
      await setFieldValue('search', searchValue);
      searchTypeRef.current = searchType;
      selectedFilterRef.current = selectedFilter;
      if (autoSubmit) {
        // The field value will not be updated until the next event loop cycle. This is a known bug
        // in formik that they aren't going to fix.
        // See https://github.com/jaredpalmer/formik/issues/529#issuecomment-571294217
        setTimeout(async () => {
          await submitForm();
        }, 0);
      }
    },
    [setFieldValue, submitForm]
  );

  const onReset = useCallback(async () => {
    await setFieldValue('search', '');
    updateSearchQuery('', 'DEFAULT_SEARCH');
  }, [setFieldValue, updateSearchQuery]);

  if (loading && !disabled) {
    return (
      <InputWrapper isActive={false}>
        <Box alignItems='center' display='flex' height={56} px={AppSpacing.MD} py={AppSpacing.SM}>
          <Skeleton preventDelay height={24} variant='rounded' width={223} />
        </Box>
      </InputWrapper>
    );
  }

  return (
    <NotificationProvider>
      <SearchDropdownProvider>
        <Search
          disabled={disabled}
          handleBlur={handleBlur}
          handleSubmit={handleSubmit}
          loading={loading}
          updateSearchValue={updateSearchValue}
          value={formikValues['search']}
          onReset={onReset}
        />
      </SearchDropdownProvider>
      <FixedBottomToast alertOverridesSx={alertOverridesSx} showIcon={false} />
    </NotificationProvider>
  );
});
